import React, { useEffect, useState } from 'react';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import { useSettings } from 'services/settings/SettingsContext';
import { buildAdminOnlyContactTypeOptions, useAdminOnlyContactTypes } from './contactTypeOptions';

// Rename the name of the function:
const ContactForm: React.FC<{ 
    viewKey?: string, 
    linkedItem?: { [key: string]: number | string }, 
    searchValue?: string 
}> = ({ 
    viewKey, linkedItem, searchValue
}) => {
    const { environmentSettings } = useSettings();
    const [defaultEntityType, setDefaultEntityType] = useState<string>(environmentSettings.contact_default_entity_type || 'organization');

    // The cancelled type is only offered inside the admin environment
    const adminOnlyTypeOptions = buildAdminOnlyContactTypeOptions(useAdminOnlyContactTypes(), 'contact.contact_type');
    const apiObject = 'contact';

    // Get the default entity type from the environment settings
    useEffect(() => {
        setDefaultEntityType(environmentSettings.contact_default_entity_type || 'organization');
    }, [environmentSettings]);

    // Parse the person name
    const parsePersonName = (searchValue: string): { title?: string, first_name?: string, last_name?: string } => {

        // Trim and clean the search value and first word
        const cleaned = searchValue.trim().replace(/\s+/g, ' ');
        const parts = cleaned.split(' ');
        const firstWord = parts[0]?.toLowerCase().replace('.', '');

        // Set the known title variables
        const knownTitles = [
            'dhr', 'de heer', 'meneer',
            'mevr', 'mevrouw',
            'mr', 'mrs',
            'fam', 'familie'
        ];

        let title;
        let nameParts = [...parts];

        // Get the title and name parts
        if (knownTitles.includes(firstWord)) {
            title = parts[0];
            nameParts = parts.slice(1);
        }

        if (nameParts.length >= 2) {
            return {
                title,
                first_name: nameParts.slice(0, -1).join(' '),
                last_name: nameParts[nameParts.length - 1]
            };
        }

        return {
            title,
            first_name: nameParts[0]
        };
    }

    // Parse the person name values from the search value
    const parsedSearchValue = searchValue ? parsePersonName(searchValue) : null;

    // Set the default contact type based on the linked item
    const defaultContactType = { value: linkedItem?.contact_type?.toString() ?? 'contact' };

    // A contact type or entity type given by the linked item is fixed and not shown as field
    const fixedContactType = linkedItem?.contact_type?.toString();
    const fixedEntityType = linkedItem?.entity_type?.toString();

    // Plug in the fields and their properties:
    const allFields: FieldType[] = [
        { 
            type: 'tabs', 
            name: 'entity_type', 
            label: 'contact.general.entity_type_label', 
            defaultValue: defaultEntityType.toString(), 
            fullWidth: true,
            options: [
                { value: 'organization', name: 'contact.entity_type.organization' },
                { value: 'person', name: 'contact.entity_type.person' },
            ],
        },
        // TODO: Make this field optional, show it in the general contact add form, but not when creating a contact from a proposal, order or job
        {
            type: 'header',
            header: 'contact.general.type_header',
            dependentOn: { fieldName: 'entity_type', value: 'organization' },
        },
        { 
            type: 'dropdown', 
            name: 'contact_type', 
            label: 'contact.general.contact_type_label', 
            disabled_selected: 'forms.select_type_disabled_selected',
            defaultValue: defaultContactType,
            allowNoneOption: false,
            showSearch: false,
            hardcodedOptions: [
                { value: 'lead', name: 'contact.contact_type.lead'},
                { value: 'client', name: 'contact.contact_type.client'},
                ...adminOnlyTypeOptions,
                { value: 'supplier', name: 'contact.contact_type.supplier'},
            ],
            dependentOn: { fieldName: 'entity_type', value: 'organization' },
        },
        // Organization fields
        {
            type: 'header',
            header: 'contact.general.organization_label',
            dependentOn: { fieldName: 'entity_type', value: 'organization' },
        },
        {
            name: 'name',
            label: 'contact.general.organization_name_label',
            type: 'text',
            ...(searchValue ? { defaultValue: searchValue } : {}),
            placeholder: 'contact.general.organization_name_placeholder',
            dependentOn: { fieldName: 'entity_type', value: 'organization' },
        },
        // Organization contact fields
        {
            type: 'header',
            header: 'Contactpersoon',
            dependentOn: { fieldName: 'entity_type', value: 'organization' },
        },
        {
            name: 'organization_person_title',
            label: 'person.general.salutation_label',
            type: 'text',
            placeholder: 'person.general.salutation_placeholder',
            dependentOn: { fieldName: 'entity_type', value: 'organization' },
            group: 'field-row',
            width: '35%'
        },
        {
            name: 'organization_person_first_name',
            label: 'person.general.first_name_label',
            type: 'text',
            placeholder: 'person.general.first_name_placeholder',
            dependentOn: { fieldName: 'entity_type', value: 'organization' },
            group: 'field-row',
            width: '65%'
        },
        {
            name: 'organization_person_last_name',
            label: 'person.general.last_name_label',
            type: 'text',
            placeholder: 'person.general.last_name_placeholder',
            dependentOn: { fieldName: 'entity_type', value: 'organization' },
        },
        {
            name: 'organization_person_phone_number',
            label: 'person.general.phone_number_label',
            type: 'text',
            placeholder: 'person.general.phone_number_placeholder',
            dependentOn: { fieldName: 'entity_type', value: 'organization' },
        },
        {
            name: 'organization_person_email',
            label: 'person.general.email_address_label',
            type: 'text',
            placeholder: 'person.general.email_address_placeholder',
            dependentOn: { fieldName: 'entity_type', value: 'organization' },
        },

        // Private person fields
        {
            type: 'header',
            header: 'contact.general.type_header',
            dependentOn: { fieldName: 'entity_type', value: 'person' },
        },
        { 
            type: 'dropdown', 
            name: 'contact_type', 
            label: 'contact.general.contact_type_label', 
            disabled_selected: 'forms.select_type_disabled_selected',
            defaultValue: defaultContactType,
            allowNoneOption: false,
            showSearch: false,
            hardcodedOptions: [
                { value: 'lead', name: 'contact.contact_type.lead'},
                { value: 'client', name: 'contact.contact_type.client'},
                ...adminOnlyTypeOptions,
            ],
            dependentOn: { fieldName: 'entity_type', value: 'person' },
        },

        // Private person
        {
            type: 'header',
            header: 'contact.general.private_person_label',
            dependentOn: { fieldName: 'entity_type', value: 'person' },
        },
        {
            name: 'title',
            label: 'person.general.salutation_label',
            type: 'text',
            placeholder: 'person.general.salutation_placeholder',
            ...(parsedSearchValue?.title ? { defaultValue: parsedSearchValue.title } : {}),
            dependentOn: { fieldName: 'entity_type', value: 'person' },
            group: 'field-row',
            width: '35%'
        },
        {
            name: 'first_name',
            label: 'person.general.first_name_label',
            type: 'text',
            placeholder: 'person.general.first_name_placeholder',
            ...(parsedSearchValue?.first_name ? { defaultValue: parsedSearchValue.first_name } : {}),
            dependentOn: { fieldName: 'entity_type', value: 'person' },
            group: 'field-row',
            width: '65%'
        },
        {
            name: 'last_name',
            label: 'person.general.last_name_label',
            type: 'text',
            placeholder: 'person.general.last_name_placeholder',
            ...(parsedSearchValue?.last_name ? { defaultValue: parsedSearchValue.last_name } : {}),
            dependentOn: { fieldName: 'entity_type', value: 'person' },
        },
        {
            name: 'phone_number',
            label: 'person.general.phone_number_label',
            type: 'text',
            placeholder: 'person.general.phone_number_placeholder',
            dependentOn: { fieldName: 'entity_type', value: 'person' },
        },
        {
            name: 'email',
            label: 'person.general.email_address_label',
            type: 'text',
            placeholder: 'person.general.email_address_placeholder',
            dependentOn: { fieldName: 'entity_type', value: 'person' },
        },
        
        // Address fields
        {
            type: 'header',
            header: 'Adres'
        },
        {
            syncWithUpdatedData: true,
            name: 'street',
            label: 'location.general.street_label',
            type: 'text',
            placeholder: 'location.general.street_placeholder',
            group: 'street-row',
            width: '70%'
        },
        {
            name: 'house_number',
            label: 'location.general.house_number_label_short',
            type: 'text',
            placeholder: 'location.general.house_number_placeholder',
            group: 'street-row',
            width: '30%'
        },
        {
            name: 'postal_code',
            label: 'location.general.postal_code_label',
            type: 'text',
            placeholder: 'location.general.postal_code_placeholder',
            group: 'city-row',
            width: '35%'
        },
        {
            syncWithUpdatedData: true,
            name: 'city',
            label: 'location.general.city_label',
            type: 'text',
            placeholder: 'location.general.city_placeholder',
            group: 'city-row',
            width: '65%'
        },
        {
            type: 'address-lookup',
        },
        {
            syncWithUpdatedData: true,
            name: 'country',
            label: 'location.general.country_label',
            type: 'text',
            placeholder: 'location.general.country_placeholder',
        },
        {
            name: 'name',
            label: 'location.general.location_name_label',
            type: 'text',
            placeholder: 'location.general.location_name_placeholder',
            tooltipText: 'location.general.location_name_tooltipText',
        },
    ];

    // Returns the entity type a field depends on, if any
    const getEntityTypeDependency = (field: FieldType): string | undefined => {
        const dependency = 'dependentOn' in field ? field.dependentOn : undefined;
        return dependency && !Array.isArray(dependency) && dependency.fieldName === 'entity_type'
            ? dependency.value?.toString()
            : undefined;
    };

    // Drop the fixed fields and keep only the fields of a fixed entity type
    const fields: FieldType[] = allFields
        .filter(field => {

            // Hide the contact type dropdown and its header when the type is fixed
            if (fixedContactType && (('name' in field && field.name === 'contact_type') || (field.type === 'header' && field.header === 'contact.general.type_header'))) {
                return false;
            }

            // Hide the entity type tabs and the fields of the other entity type when the entity type is fixed
            if (fixedEntityType) {
                if ('name' in field && field.name === 'entity_type') {
                    return false;
                }
                const entityTypeDependency = getEntityTypeDependency(field);
                if (entityTypeDependency && entityTypeDependency !== fixedEntityType) {
                    return false;
                }
            }
            return true;
        })
        .map(field => {

            // Remove the dependency on the hidden entity type tabs, the fields are always shown
            if (fixedEntityType && getEntityTypeDependency(field)) {
                const { dependentOn, ...fieldWithoutDependency } = field as FieldType & { dependentOn?: unknown };
                return fieldWithoutDependency as FieldType;
            }
            return field;
        });

    return (
        <FormModal 
            viewKey={viewKey}
            apiObject={apiObject}
            fields={fields}
            linkedItem={linkedItem}
        />
    );
};

export default ContactForm;